import { useId, useState } from 'react';
import Swal from 'sweetalert2';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { api } from '../../lib/api';
import supportedLanguages from '../../translations/en/supportedLanguages.json';
import { Button } from '../../shared/FormStyles';
import Text from '../../shared/Text';
import FlagIcon from '../../shared/FlagIcon/index.jsx';
import TranslationDisclaimer from './TranslationDisclaimer.jsx';

const RadioButton = ({ label, value, onChange, checked, isTabStop }) => {
  return (
    <ButtonContainer active={checked}>
      <FlagIcon code={value} />
      <Text
        variant="small2"
        flex="1"
        alignSelf="center"
        textAlign="left"
        pl="10px"
        color={checked ? 'primary.100' : 'black.100'}
      >
        {label}
      </Text>
      <LanguageSelector
        name="language"
        type="radio"
        value={value}
        checked={checked}
        onChange={onChange}
        // SweetAlert traps Tab and would stop on all 40 radios, so only the
        // selected one is a Tab stop; arrow keys move between the others.
        tabIndex={isTabStop ? 0 : -1}
      />
    </ButtonContainer>
  );
};

const LanguageModal = () => {
  const currentUserLanguage = useSelector(state => state.userData.language);
  const [value, setValue] = useState(currentUserLanguage);
  const { languages, suggestedLanguages } = supportedLanguages;
  const allCodes = [...suggestedLanguages, ...languages].map(l => l.code);
  const tabStop = allCodes.includes(value) ? value : allCodes[0];
  const { t } = useTranslation(['translationModal', 'shared']);
  const suggestedHeadingId = useId();
  const allHeadingId = useId();
  const handleChange = e => {
    setValue(e.target.value);
  };

  const onSubmit = e => {
    e.preventDefault();
    api({
      method: 'patch',
      url: '/api/language_settings/update_language',
      data: { language: value },
    }).then(() => location.reload());
  };
  return (
    <form onSubmit={onSubmit}>
      <Container>
        <Text
          variant="subtitle1"
          as="h3"
          mt="2px"
          mb="10px"
          textAlign="left"
          flexBasis="100%"
        >
          {t('translationModal:header')}
        </Text>
        <Text
          id={suggestedHeadingId}
          variant="subtitle2"
          as="h4"
          mt="5px"
          mb="10px"
          textAlign="left"
          flexBasis="100%"
        >
          {t('translationModal:suggestedLanguagesHeader')}
        </Text>
        <RadioGroup role="radiogroup" aria-labelledby={suggestedHeadingId}>
          {suggestedLanguages.map(language => (
            <RadioButton
              key={language.code}
              label={language.name}
              value={language.code}
              checked={language.code === value}
              isTabStop={language.code === tabStop}
              onChange={handleChange}
            />
          ))}
        </RadioGroup>
        <Text
          id={allHeadingId}
          variant="subtitle2"
          as="h4"
          mb="10px"
          textAlign="left"
          flexBasis="100%"
        >
          {t('allLanguagesHeader')}
        </Text>
        <RadioGroup role="radiogroup" aria-labelledby={allHeadingId}>
          {languages.map(language => (
            <RadioButton
              key={language.code}
              label={language.name}
              value={language.code}
              checked={language.code === value}
              isTabStop={language.code === tabStop}
              onChange={handleChange}
            />
          ))}
        </RadioGroup>
      </Container>
      <TranslationDisclaimer />
      <ButtonGroup>
        <Button variant="containedBlue" type="submit">
          {t('shared:save')}
        </Button>
        <Button variant="tertiary" type="button" onClick={() => Swal.close()}>
          {t('shared:cancel')}
        </Button>
      </ButtonGroup>
    </form>
  );
};

export default LanguageModal;

const Container = styled.div`
  overflow: auto;
  max-height: calc(100vh - 220px);
  ${themeGet('mediaQueries.forMediumUp')} {
    display: flex;
    flex-wrap: wrap;
  }
`;

const ButtonGroup = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 10px;
  &&& button {
    margin: 5px;
  }
`;

// Groups keep the old layout: one column on phones, three across from medium up.
const RadioGroup = styled.div`
  width: 100%;
  ${themeGet('mediaQueries.forMediumUp')} {
    display: flex;
    flex-wrap: wrap;
  }
`;

const ButtonContainer = styled.label`
  width: 100%;
  cursor: pointer;
  &:focus-within {
    outline: 2px solid ${themeGet('colors.primary.100')};
    outline-offset: -2px;
  }
  padding: 3px 15px;
  margin-bottom: 2px;
  display: flex;
  border-radius: 4px;
  background: ${props =>
    props.active ? themeGet('colors.primary.10') : 'transparent'};
  &:hover {
    background: ${themeGet('colors.primary.10')};
  }
  ${themeGet('mediaQueries.forMediumUp')} {
    width: 32%;
    &:hover {
      background: ${themeGet('colors.black.10')};
    }
  }
`;

// appearance: none hides the native dot but, unlike visibility: hidden, keeps
// the radio focusable and in the accessibility tree.
const LanguageSelector = styled.input`
  appearance: none;
  flex-shrink: 0;
  width: 21px;
  height: 12px;
  margin: 0;
  border: 0;
  outline: none;
  align-self: center;
  &:checked::after {
    content: '\f00c';
    font: var(--fa-font-regular);
    color: ${themeGet('colors.primary.100')};
    display: block;
  }
`;
